import React, { useState, useEffect, useRef } from 'react';
import {
  Edit2, Save, X, Bold, Italic, Heading2, Upload,
  Trash2, File, Download
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DragDropZone from '../DragDropZone';

export default function SongLyricsPanel({
  song,
  isMobile,
  loading,
  onUpdateLyricsText,
  onLyricsFileUpload,
  onDeleteLyricsFile,
  onPreviewFile,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(song.lyricsText || '');
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(song.lyricsText || '');
  }, [song.lyricsText, editing]);

  const handleSave = async () => {
    setSaving(true);
    const success = await onUpdateLyricsText(song.id, draft);
    setSaving(false);
    if (success) setEditing(false);
  };

  const handleCancel = () => {
    setDraft(song.lyricsText || '');
    setEditing(false);
  };

  const wrapSelection = (before, after = before) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = draft.substring(start, end);
    const newText = draft.substring(0, start) + before + selected + after + draft.substring(end);
    setDraft(newText);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  const insertHeading = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const before = draft.substring(0, start);
    const lineStart = before.lastIndexOf('\n') + 1;
    const newText = draft.substring(0, lineStart) + '## ' + draft.substring(lineStart);
    setDraft(newText);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + 3, start + 3);
    }, 0);
  };

  const hasText = !!song.lyricsText;
  const hasFile = !!song.lyrics;

  return (
    <div className="space-y-3">
      {/* Lyrics canvas */}
      <div className="border border-surface-border-light rounded-card-btn p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-white">Lyrics</h4>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 bg-transparent hover:bg-brand transition px-2 py-1 rounded-full text-xs"
            >
              <Edit2 className="w-3 h-3" />
              {hasText ? 'Edit' : 'Write'}
            </button>
          )}
        </div>

        {editing ? (
          <>
            <div className="flex items-center gap-1 mb-2 flex-wrap">
              <button onClick={() => wrapSelection('**')} className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition" title="Bold (**text**)">
                <Bold className="w-3.5 h-3.5 text-white" />
              </button>
              <button onClick={() => wrapSelection('*')} className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition" title="Italic (*text*)">
                <Italic className="w-3.5 h-3.5 text-white" />
              </button>
              <button onClick={insertHeading} className="p-1.5 rounded bg-white/10 hover:bg-white/20 transition" title="Section heading (## )">
                <Heading2 className="w-3.5 h-3.5 text-white" />
              </button>
              <span className="text-xs text-text-subtle ml-2">Markdown supported</span>
            </div>
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={'## Verse 1\nType your lyrics here…\n\n## Chorus'}
              className="w-full bg-transparent border border-surface-border-light rounded-card-btn px-3 py-2 text-white text-sm focus:outline-none focus:border-brand min-h-[220px] font-mono leading-relaxed"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled transition px-3 py-1.5 rounded-pill text-xs font-semibold"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="flex items-center gap-1 bg-white/10 hover:bg-white/20 transition px-3 py-1.5 rounded-pill text-xs font-semibold"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
            </div>
          </>
        ) : hasText ? (
          <div className="lyrics-rendered text-sm text-white/90 leading-relaxed">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{song.lyricsText}</ReactMarkdown>
            {song.lyricsTextUpdatedByName && (
              <p className="text-xs text-text-subtle mt-3">
                Last edited by {song.lyricsTextUpdatedByName} • {new Date(song.lyricsTextUpdatedAt).toLocaleDateString('nl-NL')}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-text-subtle italic">No lyrics yet. Click "Write" to add some.</p>
        )}
      </div>

      {/* Lyrics file (existing field) */}
      <div className="border border-surface-border-light rounded-card-btn p-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-white">Lyrics file</h4>
          <span className="text-xs text-text-subtle">Optional</span>
        </div>
        {hasFile ? (
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{song.lyrics.fileName}</p>
              <p className="text-xs text-text-subtle">{new Date(song.lyrics.uploadDate).toLocaleDateString('nl-NL')}</p>
            </div>
            <div className="flex gap-1 ml-2">
              <button
                onClick={() => onPreviewFile(song.lyrics)}
                className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                title="Preview"
              >
                <File className="w-3.5 h-3.5 text-white" />
              </button>
              <a
                href={song.lyrics.fileURL}
                download={song.lyrics.fileName}
                className="bg-transparent hover:bg-brand transition p-1.5 rounded-full"
                title="Download"
              >
                <Download className="w-3.5 h-3.5 text-white" />
              </a>
              <button
                onClick={async () => {
                  if (window.confirm('Delete lyrics file?')) {
                    await onDeleteLyricsFile(song.id, song.lyrics.storagePath);
                  }
                }}
                className="bg-transparent hover:bg-red-600 transition p-1.5 rounded-full"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          </div>
        ) : (
          <DragDropZone
            accept=".docx,.pdf,.txt,.md"
            onFileSelect={(file) => onLyricsFileUpload(song.id, file)}
            disabled={loading}
            icon={Upload}
            message="Drop a lyrics file (.docx, .pdf, .txt, .md)"
            isMobile={isMobile}
          />
        )}
      </div>

      <style>{`
        .lyrics-rendered h1 { font-size: 1.1rem; font-weight: 700; margin: 0.75rem 0 0.25rem; color: white; }
        .lyrics-rendered h2 { font-size: 0.95rem; font-weight: 600; margin: 0.75rem 0 0.25rem; color: #C500FF; text-transform: uppercase; letter-spacing: 0.05em; }
        .lyrics-rendered h3 { font-size: 0.9rem; font-weight: 600; margin: 0.5rem 0 0.25rem; color: white; }
        .lyrics-rendered p { margin: 0.25rem 0; }
        .lyrics-rendered strong { font-weight: 700; }
        .lyrics-rendered em { font-style: italic; }
        .lyrics-rendered ul, .lyrics-rendered ol { margin: 0.5rem 0 0.5rem 1.5rem; }
        .lyrics-rendered ul { list-style: disc; }
        .lyrics-rendered ol { list-style: decimal; }
      `}</style>
    </div>
  );
}
